import type { Player } from "@cloudnest/horror-race-engine";
import { Fragment, useEffect, useRef, useState } from "react";
import { getHorrorPlayerToken } from "../utils/horror-tokens";
import { buildRosterRail } from "../utils/turn-rail";
import { PlayerList } from "./PlayerList";

/**
 * D3 rail modes:
 * - "turn" (default): dot state = current turn (caret/passed dim/2-pass doubling).
 * - "ready": dot state = per-player ready flag (confirm/selection done). Confirmed
 *   = gold ring + ✓ badge, pending = dim/grayscale. No caret/pass/doubling. 선공
 *   (turnOrder[0]) keeps a small accent caret to mark next-race start player.
 */
export type TopBannerMode = "turn" | "ready";

interface TopBannerProps {
  readonly players: readonly Player[];
  readonly turnOrder: readonly number[];
  readonly currentTurnIndex: number;
  readonly drawPass: number;
  readonly forfeitPlayerIndex: number | null;
  /** isFewPlayerVariant(playerCount) — ≤5 players ⇒ doubled rail. */
  readonly fewPlayer: boolean;
  readonly round: number;
  readonly raceNumber: number;
  readonly totalRaces: number;
  readonly garnets: Readonly<Record<string, number>>;
  readonly myPlayerId: string | null;
  readonly showSecretFog?: boolean;
  /** D3 rail mode — defaults to "turn". */
  readonly mode?: TopBannerMode;
  /**
   * Per-player ready flags keyed by `player.id`. Used when `mode === "ready"`.
   * Sources: SETUP=`secretSelection !== null`, ROUND_RECAP=`hasConfirmedRoundRecap`,
   * RACE_SCORING=`hasConfirmedRecap`. Missing keys treated as false.
   */
  readonly readyFlags?: Readonly<Record<string, boolean>>;
}

/**
 * T6 + T16 + T17 + T19 + T20 — persistent top roster banner.
 * - T17: rendered in TURN ORDER (round start player first); few-player (≤5)
 *   games double each player (1패스 | 2패스); a 토큰 포기 player's dot gets an X;
 *   on round change the new start player briefly pulses (강조만/minimal transition).
 * - T16: the whole banner is a button → opens a lighter (no-scrim) dropdown popup
 *   listing players + garnets (reuses PlayerList).
 * - T19: responsive — dots are centered so up to 12 fit the min screen (390px)
 *   with no scrollbar; on overflow the edges fade/blur and the current dot is
 *   auto-centered. The whole banner triggers; a centered bottom hint signals
 *   expand. The popup opens FULL WIDTH (flush under the banner, capped + centered).
 * - T20: collapsed view simplified — names are always hidden (full names live in
 *   the popup), dots are small (20px), the ★ start marker is gone, and the current
 *   turn is shown by an enabled/disabled scheme (passed dim, current full + a caret
 *   beneath) instead of a footprint-inflating ring; the 2패스 divider has no label.
 */
export function TopBanner({
  players,
  turnOrder,
  currentTurnIndex,
  drawPass,
  forfeitPlayerIndex,
  fewPlayer,
  round,
  raceNumber,
  totalRaces,
  garnets,
  myPlayerId,
  showSecretFog = false,
  mode = "turn",
  readyFlags,
}: TopBannerProps) {
  const isReady = mode === "ready";
  const [open, setOpen] = useState(false);
  // 강조만 (minimal) round transition: pulse the new leader on round change.
  const [pulse, setPulse] = useState(false);
  const prevRound = useRef(round);
  useEffect(() => {
    if (prevRound.current === round) return;
    prevRound.current = round;
    setPulse(true);
    const t = setTimeout(() => setPulse(false), 650);
    return () => clearTimeout(t);
  }, [round]);

  // Ready mode is a single pass with no caret/passed/forfeit semantics — render a
  // flat per-player rail in turnOrder so 선공 (turnOrder[0]) sits at the front.
  const rail = isReady
    ? turnOrder.map((playerIndex, pos) => ({
        playerIndex,
        pass: 1 as const,
        isStart: pos === 0,
        isCurrent: false,
        isPassed: false,
        isForfeit: false,
        key: `r-${playerIndex}`,
      }))
    : buildRosterRail({
        turnOrder,
        currentTurnIndex,
        drawPass,
        forfeitPlayerIndex,
        fewPlayer,
      });

  // T19 — overflow fallback: when the dot row still can't fit (sub-390 / odd
  // layouts) fade+blur the edges and keep the current-turn dot centered instead
  // of showing a scrollbar. Only runs in the browser (effects skip SSR/unit render).
  const scrollRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState(false);
  const railKey = rail.map((s) => s.key).join("|");
  // biome-ignore lint/correctness/useExhaustiveDependencies: railKey/currentTurnIndex/drawPass are re-measure triggers; the body reads layout from the DOM ref.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => {
      const over = el.scrollWidth > el.clientWidth + 1;
      setOverflow(over);
      if (!over) return;
      const active = el.querySelector('[data-is-current="true"]');
      if (!(active instanceof HTMLElement)) return;
      // Content-coordinate left of the active dot (origin-independent — avoids
      // relying on offsetParent), then offset so it lands at the viewport center.
      const left =
        active.getBoundingClientRect().left -
        el.getBoundingClientRect().left +
        el.scrollLeft -
        el.clientWidth / 2 +
        active.offsetWidth / 2;
      const reduce = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      el.scrollTo({ left, behavior: reduce ? "auto" : "smooth" });
    };
    measure();
    // rAF-defer the observer callback so a resize → setOverflow → re-render →
    // resize cycle can't thrash layout synchronously on WebKit.
    const ro = new ResizeObserver(() => requestAnimationFrame(measure));
    ro.observe(el);
    return () => ro.disconnect();
  }, [railKey, currentTurnIndex, drawPass]);

  return (
    <div style={{ position: "relative" }}>
      {/* biome-ignore lint/a11y/useSemanticElements: the roster row scrolls and
          contains its own buttons; a div with role=button avoids nested buttons. */}
      <div
        data-testid="horror-top-banner"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-controls="horror-player-popup"
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        className="relative cursor-pointer overflow-hidden"
        style={{
          borderBottom: "1px solid var(--cn-horror-surface-border)",
          backgroundColor: "var(--cn-horror-surface-secondary)",
        }}
      >
        <div
          ref={scrollRef}
          data-testid="horror-rail-scroll"
          className={`hr-rail-scroll flex items-center gap-1 overflow-x-auto ${
            overflow ? "hr-rail-overflow" : ""
          }`}
          style={{ padding: "6px 12px 7px" }}
        >
          {rail.map((slot, i) => {
            const player = players[slot.playerIndex];
            if (!player) return null;
            const accent = getHorrorPlayerToken(slot.playerIndex);
            const showSep = i > 0 && rail[i - 1]?.pass !== slot.pass;
            const confirmed = isReady && (readyFlags?.[player.id] ?? false);
            // Ready mode opacity: confirmed full, pending dim+grayscale.
            // Turn mode opacity: current full, passed very dim+grayscale, future mid.
            const dotOpacity = isReady
              ? confirmed
                ? 1
                : 0.34
              : slot.isCurrent
                ? 1
                : slot.isPassed
                  ? 0.3
                  : 0.62;
            const dotFilter = isReady
              ? confirmed
                ? undefined
                : "grayscale(0.4)"
              : slot.isPassed
                ? "grayscale(0.55)"
                : undefined;
            const dotBoxShadow = confirmed
              ? "0 0 0 2px var(--cn-horror-finished-flash), 0 0 9px rgba(255,215,0,0.5)"
              : undefined;
            // Title text — adjust for ready mode (no "현재 차례", show ready state).
            const title = isReady
              ? `${player.name}${slot.isStart ? " (선공)" : ""}${
                  confirmed ? " · 확인 완료" : " · 대기"
                }`
              : `${player.name}${slot.isStart ? " (선공)" : ""}${
                  slot.isCurrent ? " ◀ 현재 차례" : ""
                }${slot.isForfeit ? " · 토큰 포기" : ""}${
                  fewPlayer ? ` · ${slot.pass}패스` : ""
                }`;

            return (
              <Fragment key={slot.key}>
                {/* T20 — 2패스 divider: bare line, no label, tight margins.
                    (Ready mode is single-pass so this never renders.) */}
                {showSep && (
                  <span
                    aria-hidden="true"
                    className="w-px shrink-0 self-center"
                    style={{
                      height: 16,
                      margin: "0 1px",
                      backgroundColor: "var(--cn-horror-lane-divider)",
                    }}
                  />
                )}
                <div
                  data-testid="horror-banner-player"
                  data-player-index={slot.playerIndex}
                  data-pass={slot.pass}
                  data-mode={mode}
                  data-is-current={slot.isCurrent ? "true" : "false"}
                  data-is-start={slot.isStart ? "true" : "false"}
                  data-is-passed={slot.isPassed ? "true" : "false"}
                  data-is-forfeit={slot.isForfeit ? "true" : "false"}
                  data-is-confirmed={
                    isReady ? (confirmed ? "true" : "false") : undefined
                  }
                  className="relative flex shrink-0 flex-col items-center"
                  style={{ paddingBottom: 5 }}
                  title={title}
                >
                  <div
                    className={`relative flex h-5 w-5 items-center justify-center rounded-full font-bold text-[8px] ${
                      slot.isForfeit ? "hr-rail-forfeit" : ""
                    } ${pulse && slot.isStart ? "hr-rail-pulse" : ""}`}
                    style={{
                      backgroundColor: accent,
                      color: "#fff",
                      opacity: dotOpacity,
                      filter: dotFilter,
                      boxShadow: dotBoxShadow,
                      transition:
                        "box-shadow .25s ease, opacity .25s ease, filter .25s ease",
                    }}
                    aria-current={slot.isCurrent ? "true" : undefined}
                  >
                    {slot.playerIndex + 1}
                  </div>
                  {/* Ready mode: ✓ chip overlay on confirmed dots. */}
                  {confirmed && (
                    <span
                      data-testid="horror-rail-confirm-chk"
                      aria-hidden="true"
                      className="absolute flex items-center justify-center font-black"
                      style={{
                        right: -3,
                        top: -4,
                        width: 13,
                        height: 13,
                        borderRadius: "50%",
                        backgroundColor: "var(--cn-horror-finished-flash)",
                        color: "#1a1a1a",
                        fontSize: 9,
                      }}
                    >
                      ✓
                    </span>
                  )}
                  {/* T20 turn mode — current-turn caret beneath the dot. */}
                  {!isReady && slot.isCurrent && (
                    <span
                      data-testid="horror-rail-current-caret"
                      aria-hidden="true"
                      className="absolute"
                      style={{
                        left: "50%",
                        bottom: 0,
                        transform: "translateX(-50%)",
                        width: 0,
                        height: 0,
                        borderLeft: "3px solid transparent",
                        borderRight: "3px solid transparent",
                        borderBottom: `4px solid ${accent}`,
                      }}
                    />
                  )}
                  {/* Ready mode — 선공 caret beneath the first dot (next-race start). */}
                  {isReady && slot.isStart && (
                    <span
                      data-testid="horror-rail-start-caret"
                      aria-hidden="true"
                      className="absolute"
                      style={{
                        left: "50%",
                        bottom: 0,
                        transform: "translateX(-50%)",
                        width: 0,
                        height: 0,
                        borderLeft: "3px solid transparent",
                        borderRight: "3px solid transparent",
                        borderBottom: `4px solid ${accent}`,
                      }}
                    />
                  )}
                </div>
              </Fragment>
            );
          })}
        </div>

        {/* T19 — whole banner is the trigger; a small centered hint at the bottom
            signals expandability (the old far-right chevron overlay is gone). */}
        <span
          data-testid="horror-rail-hint"
          aria-hidden="true"
          className="pointer-events-none absolute flex"
          style={{
            left: "50%",
            bottom: 1,
            transform: open
              ? "translateX(-50%) rotate(180deg)"
              : "translateX(-50%)",
            color: open ? "var(--cn-horror-p3)" : "var(--cn-horror-text-muted)",
            opacity: open ? 1 : 0.55,
            transition:
              "transform 280ms cubic-bezier(0.34,1.56,0.64,1), opacity 0.15s, color 0.15s",
          }}
        >
          <svg
            width="14"
            height="8"
            viewBox="0 0 16 8"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M2 2l6 4 6-4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </div>

      {open && (
        <>
          {/* lighter dropdown — no scrim: transparent click-catcher to close */}
          <div
            onClick={() => setOpen(false)}
            aria-hidden="true"
            style={{ position: "fixed", inset: 0, zIndex: 40 }}
          />
          <div
            id="horror-player-popup"
            data-testid="horror-player-popup"
            role="dialog"
            aria-label="플레이어 목록"
            className="hr-roster-popup absolute overflow-hidden"
            style={{
              top: "100%",
              left: 0,
              right: 0,
              maxWidth: 520,
              margin: "0 auto",
              zIndex: 50,
              backgroundColor: "var(--cn-horror-card-bg)",
              border: "1px solid var(--cn-horror-card-border)",
              borderTop: 0,
              borderRadius: "0 0 12px 12px",
              boxShadow: "0 16px 32px rgba(0,0,0,0.55)",
            }}
          >
            <div
              className="flex items-center justify-between"
              style={{
                padding: "10px 12px",
                borderBottom: "1px solid var(--cn-horror-surface-border)",
              }}
            >
              <span>
                <span
                  className="font-extrabold text-[13px]"
                  style={{ color: "var(--cn-horror-text)" }}
                >
                  플레이어
                </span>
                <span
                  className="ml-1.5 font-mono text-[10px]"
                  style={{ color: "var(--cn-horror-text-muted)" }}
                >
                  레이스 {raceNumber}/{totalRaces} · {round}라운드
                </span>
              </span>
              <button
                type="button"
                aria-label="닫기"
                onClick={() => setOpen(false)}
                className="cursor-pointer border-0 bg-transparent text-[14px] leading-none"
                style={{ color: "var(--cn-horror-text-muted)" }}
              >
                ✕
              </button>
            </div>
            <PlayerList
              players={players}
              turnOrder={turnOrder}
              currentTurnIndex={currentTurnIndex}
              myPlayerId={myPlayerId}
              garnets={garnets}
              showSecretFog={showSecretFog}
              showHeading={false}
            />
          </div>
        </>
      )}
    </div>
  );
}
